import { useCallback, useContext } from 'react';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';

const useDevMailFactory = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  const getBookingWidgets = useCallback(async () => {
    const response = await sendRequest({
      url: '/api/v1/bookingWidget/ua',
      method: 'GET',
    });

    return Array.isArray(response) ? response : [];
  }, [sendRequest]);

  const getKnowledgeBaseArticles = useCallback(
    async ({ status = 'published', limit = 200, skip = 0 } = {}) => {
      const params = { limit, skip };
      if (status) params.status = status;

      const response = await sendRequest({
        url: '/api/v1/knowledge-base',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
      });

      return response?.items ?? [];
    },
    [authCtx, sendRequest]
  );

  const getScenarios = useCallback(async () => {
    const response = await sendRequest({
      url: '/api/v1/dev-tools/mail-factory/program/scenarios',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });

    return response?.scenarios ?? [];
  }, [authCtx, sendRequest]);

  const getRecentBookings = useCallback(async () => {
    const response = await sendRequest({
      url: '/api/v1/dev-tools/mail-factory/program/bookings',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });

    return response?.bookings ?? [];
  }, [authCtx, sendRequest]);

  const getProgramDefaults = useCallback(
    async ({ scenarioKey, bookingId, bookingWidgetId, knowledgeBaseArticleId }) => {
      const params = {};
      if (scenarioKey) params.scenarioKey = scenarioKey;
      if (bookingId) params.bookingId = bookingId;
      if (bookingWidgetId) params.bookingWidgetId = bookingWidgetId;
      if (knowledgeBaseArticleId) params.knowledgeBaseArticleId = knowledgeBaseArticleId;

      return await sendRequest({
        url: '/api/v1/dev-tools/mail-factory/program/defaults',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
      });
    },
    [authCtx, sendRequest]
  );

  const getReplyableThreads = useCallback(async () => {
    const response = await sendRequest({
      url: '/api/v1/dev-tools/mail-factory/reply/threads',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });

    return response?.threads ?? [];
  }, [authCtx, sendRequest]);

  const getReplyDefaults = useCallback(
    async ({ threadNumber }) => {
      const params = {};
      if (threadNumber) params.threadNumber = threadNumber;

      return await sendRequest({
        url: '/api/v1/dev-tools/mail-factory/reply/defaults',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
      });
    },
    [authCtx, sendRequest]
  );

  const createProgramMail = useCallback(
    async ({ mail }) => {
      return await sendRequest({
        url: '/api/v1/dev-tools/mail-factory/program',
        method: 'POST',
        headers: {
          ...authCtx.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: { mail },
      });
    },
    [authCtx, sendRequest]
  );

  return {
    getBookingWidgets,
    getKnowledgeBaseArticles,
    getScenarios,
    getRecentBookings,
    getProgramDefaults,
    getReplyableThreads,
    getReplyDefaults,
    createProgramMail,
  };
};

export default useDevMailFactory;
